import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  Video, Camera, Users, CheckCircle2, ArrowRight,
  Play, Sparkles, Calendar, Image as ImageIcon, LayoutGrid,
  Palette, Eye, MessageSquare, Send, FileText,
  X, Shield, Timer, ExternalLink,
  Layers, Megaphone, BarChart3, TrendingUp,
  Package, ClipboardCheck, Lock, AlertTriangle, KeyRound,
  PenLine
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "react-router-dom";
import ServiceHero, { Highlight } from "./ServiceHero";
import IncludesExcludes from "./IncludesExcludes";


import instagramIcon from "@/assets/partners/instagram-icon.webp";
import facebookIcon from "@/assets/partners/facebook-icon.webp";
import tiktokIcon from "@/assets/partners/tiktok-icon.webp";

/* ───── SCROLL ANIMATION ───── */
function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setIsVisible(true); obs.unobserve(el); } }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, isVisible };
}

function AnimateIn({ children, className = "", delay = 0 }: { children: ReactNode; className?: string; delay?: number }) {
  const { ref, isVisible } = useInView();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(30px)",
        transition: `opacity 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}s, transform 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}s`,
      }}
    >
      {children}
    </div>
  );
}

/* ───── PLATFORM LOGOS ───── */
const InstagramLogo = ({ className = "w-8 h-8" }: { className?: string }) => (
  <img src={instagramIcon} alt="Instagram" className={`${className} object-contain`} />
);
const LinkedInLogo = ({ className = "w-8 h-8" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className}>
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" fill="#0A66C2"/>
  </svg>
);
const FacebookLogo = ({ className = "w-8 h-8" }: { className?: string }) => (
  <img src={facebookIcon} alt="Facebook" className={`${className} object-contain`} />
);
const TikTokLogo = ({ className = "w-8 h-8" }: { className?: string }) => (
  <img src={tiktokIcon} alt="TikTok" className={`${className} object-contain`} />
);
const YoutubeLogo = ({ className = "w-8 h-8" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className}>
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" fill="#FF0000"/>
  </svg>
);

const platforms = [
  { id: "instagram", name: "Instagram", Logo: InstagramLogo },
  { id: "facebook", name: "Facebook", Logo: FacebookLogo },
  { id: "tiktok", name: "TikTok", Logo: TikTokLogo },
  { id: "linkedin", name: "LinkedIn", Logo: LinkedInLogo },
  { id: "youtube", name: "YouTube Shorts", Logo: YoutubeLogo },
];

/* ───── COMPONENT ───── */
const SocialDemo = () => {
  const [hoveredPlatform, setHoveredPlatform] = useState<string | null>(null);

  return (
    <div className="space-y-32 overflow-hidden">

      {/* ━━━ HERO ━━━ */}
      <ServiceHero
        eyebrow="Redes Sociales"
        title={<>Gestión de <Highlight>Redes Sociales</Highlight></>}
        description={<>Contenido profesional y constante para tus redes. Diseñamos, creamos y publicamos para que tu marca esté siempre activa y cuidada, sin que tengas que preocuparte de nada.</>}
        decoration={
          <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4">
            {platforms.map((p, i) => (
              <div
                key={p.id}
                className="flex flex-col items-center gap-2 group cursor-pointer"
                onMouseEnter={() => setHoveredPlatform(p.id)}
                onMouseLeave={() => setHoveredPlatform(null)}
                style={{ animation: `float 6s ease-in-out infinite`, animationDelay: `${i * 0.6}s` }}
              >
                <div className={`w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-card border shadow-md flex items-center justify-center transition-all duration-500 ${hoveredPlatform === p.id ? "shadow-glow border-primary/30 scale-110" : "border-border"}`}>
                  <p.Logo className="w-7 h-7" />
                </div>
                <span className={`text-[10px] text-muted-foreground font-medium transition-opacity ${hoveredPlatform === p.id ? "opacity-100" : "opacity-60"}`}>{p.name}</span>
              </div>
            ))}
          </div>
        }
        primaryCta={{ label: "Contratar Gestión de Redes", href: "/#pricing" }}
        secondaryCta={{ label: "Agendar reunión", href: "/#contact" }}
        includes={["8 piezas/mes", "Publicación multicanal", "Calendario aprobado", "Reporte mensual"]}
      />

      <IncludesExcludes
        includes={[
          "<strong>12 piezas al mes: 4 posts estáticos + 4 carruseles + 4 vídeos verticales</strong>",
          "Vídeos en 3 modalidades a tu elección: (a) te pasamos guiones, te grabas hablando y nosotros editamos · (b) te damos indicaciones de tomas, grabas tu negocio/productos y nosotros editamos con narración · (c) vídeos enteros con IA + stock + narración",
          "Calendario editorial mensual para tu aprobación (72h)",
          "Hashtags incluidos",
          "Adaptación de cada pieza al formato de cada red",
          "Publicación en todas tus redes activas",
          "Programación con Metricool",
          "Informe automático vía Metricool",
          "2 tandas de revisión por pieza",
        ]}
        excludes={[
          "Stories",
          "Community management (responder comentarios y mensajes)",
          "Atención a tus clientes",
          "Producción de vídeo profesional con cámara",
          "Sesiones fotográficas presenciales",
          "Gestión de crisis de reputación",
          "Publicidad de pago (eso es Meta/TikTok Ads)",
        ]}
      />

      {/* ━━━ 3 COLUMNAS RESUMEN ━━━ */}
      <section className="max-w-5xl mx-auto">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            {
              icon: <Package className="w-7 h-7" />,
              title: "Qué se entrega",
              items: [
                "8 piezas de contenido al mes",
                "3 posts estáticos",
                "3 carruseles",
                "2 vídeos verticales",
                "Publicación en todas tus redes",
              ],
            },
            {
              icon: <Calendar className="w-7 h-7" />,
              title: "Cuándo se entrega",
              items: [
                "Calendario y contenido del mes en los primeros 14 días",
                "Publicación programada durante todo el mes",
              ],
            },
            {
              icon: <BarChart3 className="w-7 h-7" />,
              title: "Reporting mensual",
              items: [
                "Informe de alcance, engagement y crecimiento",
                "Entregado el día de tu ciclo de facturación, cada mes",
              ],
            },
          ].map((col, idx) => (
            <AnimateIn key={idx} delay={idx * 0.1}>
              <Card className="h-full border-primary/15 bg-gradient-to-br from-primary/[0.05] to-transparent hover:shadow-[0_8px_30px_-12px_hsl(var(--primary)/0.3)] hover:-translate-y-1 transition-all duration-500">
                <CardContent className="p-6">
                  <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center text-primary mb-4">
                    {col.icon}
                  </div>
                  <h4 className="font-bold text-lg mb-4">{col.title}</h4>
                  <ul className="space-y-2">
                    {col.items.map((it, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <CheckCircle2 size={14} className="text-primary shrink-0 mt-0.5" />
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </AnimateIn>
          ))}
        </div>
      </section>


      {/* ━━━ SETUP INICIAL ━━━ */}
      <section className="max-w-4xl mx-auto">
        <AnimateIn>
          <div className="text-center mb-10">
            <Badge variant="secondary" className="mb-4">Primeros 14 días</Badge>
            <h3 className="text-3xl md:text-4xl font-bold mb-3">Puesta en marcha</h3>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Al contratar el servicio, durante los primeros 14 días definimos tu estrategia de contenido y preparamos tu primer mes de publicaciones.
            </p>
          </div>
        </AnimateIn>

        <AnimateIn delay={0.1}>
          <Card className="border-primary/15 bg-gradient-to-br from-primary/[0.04] to-transparent">
            <CardContent className="p-6 md:p-8">
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  { icon: <PenLine className="w-5 h-5" />, text: "Estrategia inicial de contenido (ejes temáticos, tono y frecuencia)" },
                  { icon: <Palette className="w-5 h-5" />, text: "Definición de tu identidad visual para redes" },
                  { icon: <Calendar className="w-5 h-5" />, text: "Primer calendario editorial del mes" },
                  { icon: <Send className="w-5 h-5" />, text: "Configuración de la programación en Metricool" },
                ].map((it, i) => (
                  <div key={i} className="flex items-start gap-3 p-4 rounded-xl bg-card border border-border/60">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary shrink-0">
                      {it.icon}
                    </div>
                    <p className="text-sm pt-1.5">{it.text}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </AnimateIn>
      </section>

      {/* ━━━ TRABAJO MENSUAL ━━━ */}
      <section>
        <AnimateIn>
          <div className="text-center mb-14">
            <Badge variant="secondary" className="mb-4">Cada mes</Badge>
            <h3 className="text-3xl md:text-4xl font-bold mb-3">Tu servicio mes a mes</h3>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Cada mes producimos y publicamos tu contenido para mantener tus redes activas y profesionales. Esto es exactamente lo que incluye tu suscripción:
            </p>
          </div>
        </AnimateIn>

        <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-6">
          {[
            { icon: <ImageIcon className="w-8 h-8" />, count: "3", title: "Posts estáticos", desc: "Diseños gráficos profesionales adaptados a tu marca y plataformas.", color: "from-chart-1/10 to-chart-1/5", border: "border-chart-1/20" },
            { icon: <Layers className="w-8 h-8" />, count: "3", title: "Carruseles", desc: "Contenido educativo o showcase de tus productos y servicios.", color: "from-chart-3/10 to-chart-3/5", border: "border-chart-3/20" },
            { icon: <Video className="w-8 h-8" />, count: "2", title: "Vídeos verticales", desc: "Reels y TikToks con cortes dinámicos, subtítulos y música.", color: "from-chart-5/10 to-chart-5/5", border: "border-chart-5/20" },
          ].map((item, idx) => (
            <AnimateIn key={idx} delay={idx * 0.1}>
              <Card className={`group h-full ${item.border} hover:shadow-lg hover:-translate-y-1 transition-all duration-500 overflow-hidden`}>
                <CardContent className="p-0">
                  <div className={`p-6 bg-gradient-to-br ${item.color}`}>
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-14 h-14 rounded-2xl bg-card border border-border shadow-sm flex items-center justify-center text-primary group-hover:scale-110 transition-transform duration-500">
                        {item.icon}
                      </div>
                      <div className="text-right">
                        <div className="text-4xl font-bold text-primary">{item.count}</div>
                        <div className="text-[10px] text-muted-foreground uppercase tracking-wider">al mes</div>
                      </div>
                    </div>
                    <h4 className="font-bold text-lg mb-2">{item.title}</h4>
                    <p className="text-sm text-muted-foreground">{item.desc}</p>
                  </div>
                </CardContent>
              </Card>
            </AnimateIn>
          ))}
        </div>

        <AnimateIn delay={0.4}>
          <div className="max-w-4xl mx-auto mt-8">
            <Card className="border-primary/10">
              <CardContent className="p-6">
                <div className="grid sm:grid-cols-2 gap-3">
                  {[
                    { icon: <ClipboardCheck className="w-4 h-4" />, text: "Calendario editorial entregado para tu aprobación" },
                    { icon: <LayoutGrid className="w-4 h-4" />, text: "Adaptación de cada pieza al formato de cada red social" },
                    { icon: <Send className="w-4 h-4" />, text: "Publicación en Instagram, Facebook, TikTok, LinkedIn y YouTube Shorts" },
                    { icon: <Calendar className="w-4 h-4" />, text: "Programación con Metricool" },
                    { icon: <BarChart3 className="w-4 h-4" />, text: "Reporte mensual de alcance, engagement y crecimiento" },
                  ].map((it, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 border border-border/60">
                      <span className="text-primary">{it.icon}</span>
                      <span className="text-sm">{it.text}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </AnimateIn>
      </section>

      {/* ━━━ APROBACIÓN DEL CALENDARIO (DESTACADA) ━━━ */}
      <section className="max-w-4xl mx-auto">
        <AnimateIn>
          <div className="relative">
            <div className="absolute -inset-6 bg-primary/10 rounded-[2.5rem] blur-3xl pointer-events-none" />
            <Card className="relative border-2 border-primary/40 bg-gradient-to-br from-primary/[0.08] via-primary/[0.04] to-transparent">
              <CardContent className="p-8 md:p-10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-2xl bg-primary/15 flex items-center justify-center text-primary">
                    <ClipboardCheck className="w-6 h-6" />
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold">Tú apruebas antes de publicar</h3>
                </div>
                <p className="text-base text-muted-foreground mb-3">
                  Cada mes te entregamos el <strong className="text-foreground">calendario editorial completo para tu aprobación</strong> antes de publicar nada. Así tienes siempre el control de lo que se publica en tus redes.
                </p>
                <p className="text-base text-muted-foreground">
                  Dispones de <strong className="text-foreground">72h para revisar y aprobar el calendario</strong>. Pasado ese plazo, se da por aprobado y se procede a la programación, para que tu contenido salga siempre a tiempo y tus redes no se queden paradas.
                </p>
              </CardContent>
            </Card>
          </div>
        </AnimateIn>
      </section>

      {/* ━━━ QUÉ NO INCLUYE ━━━ */}
      <section className="max-w-4xl mx-auto">
        <AnimateIn>
          <div className="text-center mb-10">
            <Badge variant="secondary" className="mb-4">Transparencia</Badge>
            <h3 className="text-3xl md:text-4xl font-bold mb-3">Qué no incluye este servicio</h3>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Para que todo quede claro desde el principio, estos puntos quedan fuera del servicio:
            </p>
          </div>
        </AnimateIn>

        <AnimateIn delay={0.1}>
          <div className="rounded-2xl border border-border overflow-hidden divide-y divide-border">
            {[
              { icon: <MessageSquare className="w-5 h-5" />, title: "Community management", desc: "Responder comentarios y mensajes directos lo gestionas tú." },
              { icon: <Users className="w-5 h-5" />, title: "Atención al cliente en redes", desc: "No respondemos consultas de clientes a través de las redes." },
              { icon: <Video className="w-5 h-5" />, title: "Producción de vídeo profesional", desc: "No incluye grabación con cámara y equipo profesional." },
              { icon: <Camera className="w-5 h-5" />, title: "Sesiones fotográficas presenciales", desc: "Sin sesiones de fotos en estudio o exteriores." },
              { icon: <AlertTriangle className="w-5 h-5" />, title: "Gestión de crisis de reputación", desc: "Las crisis se gestionan en consultoría aparte." },
              { icon: <Megaphone className="w-5 h-5" />, title: "Campañas de publicidad de pago", desc: "Son servicios separados: Meta Ads y TikTok Ads.", link: "/servicios/publicidad-meta", linkText: "Ver publicidad →" },
            ].map((item, idx) => (
              <div key={idx} className="flex items-center gap-4 p-5 hover:bg-muted/30 transition-colors">
                <div className="w-10 h-10 rounded-xl bg-destructive/10 flex items-center justify-center text-destructive shrink-0">
                  <X className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="font-semibold text-sm">{item.title}</span>
                  <span className="text-muted-foreground text-sm ml-2 hidden sm:inline">— {item.desc}</span>
                  <p className="text-muted-foreground text-xs sm:hidden">{item.desc}</p>
                </div>
                {item.link && (
                  <Link to={item.link} className="text-xs font-medium text-primary hover:underline shrink-0">
                    {item.linkText}
                  </Link>
                )}
              </div>
            ))}
          </div>
        </AnimateIn>
      </section>

      {/* ━━━ CÓMO TRABAJAMOS ━━━ */}
      <section>
        <AnimateIn>
          <div className="text-center mb-14">
            <Badge variant="secondary" className="mb-4">Proceso</Badge>
            <h3 className="text-3xl md:text-4xl font-bold mb-3">Cómo trabajamos</h3>
          </div>
        </AnimateIn>

        <div className="max-w-5xl mx-auto grid md:grid-cols-5 gap-4">
          {[
            { step: "01", icon: <Sparkles className="w-5 h-5" />, title: "Contratas el servicio", desc: "Eliges Gestión de Redes en tu plan." },
            { step: "02", icon: <KeyRound className="w-5 h-5" />, title: "Nos das acceso", desc: "A tus redes sociales y material de marca." },
            { step: "03", icon: <Palette className="w-5 h-5" />, title: "Setup en 14 días", desc: "Estrategia, identidad visual y primer calendario." },
            { step: "04", icon: <ClipboardCheck className="w-5 h-5" />, title: "Apruebas el calendario", desc: "Revisas y das el visto bueno al contenido del mes." },
            { step: "05", icon: <Send className="w-5 h-5" />, title: "Publicamos y reportamos", desc: "Programamos todo y recibes tu informe el día de tu ciclo de facturación." },
          ].map((s, idx) => (
            <AnimateIn key={idx} delay={idx * 0.08}>
              <Card className="h-full border-primary/15 hover:border-primary/40 hover:shadow-md transition-all duration-500">
                <CardContent className="p-5 flex flex-col items-center text-center">
                  <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center text-primary mb-3">
                    {s.icon}
                  </div>
                  <span className="text-xs font-bold text-primary mb-1">{s.step}</span>
                  <h4 className="font-semibold text-sm mb-1">{s.title}</h4>
                  <p className="text-xs text-muted-foreground leading-relaxed">{s.desc}</p>
                </CardContent>
              </Card>
            </AnimateIn>
          ))}
        </div>
      </section>

      {/* ━━━ REVISIONES ━━━ */}
      <section className="max-w-3xl mx-auto">
        <AnimateIn>
          <Card className="border-primary/20 bg-gradient-to-br from-primary/[0.05] to-transparent">
            <CardContent className="p-8 md:p-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center text-primary">
                  <PenLine className="w-6 h-6" />
                </div>
                <h3 className="text-2xl font-bold">Revisiones de contenido</h3>
              </div>
              <p className="text-muted-foreground mb-4">
                Cada pieza de contenido incluye <strong className="text-foreground">2 tandas de revisión</strong>. Una tanda equivale a un único mensaje con todos los cambios que solicitas para una pieza.
              </p>
              <p className="text-sm text-muted-foreground flex items-start gap-2">
                <Timer size={14} className="text-primary shrink-0 mt-0.5" />
                Dispones de 72h para enviar tus cambios desde que recibes la pieza. Pasado ese plazo, se da por aprobada.
              </p>
            </CardContent>
          </Card>
        </AnimateIn>
      </section>

      {/* ━━━ CTA FINAL ━━━ */}
      <section className="relative max-w-4xl mx-auto">
        <div className="absolute -inset-10 bg-primary/5 rounded-[3rem] blur-3xl pointer-events-none" />

        <AnimateIn>
          <Card className="border-primary/20 overflow-hidden relative">
            <CardContent className="p-10 md:p-16 text-center relative z-10">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center text-primary mx-auto mb-6">
                <Sparkles className="w-8 h-8" />
              </div>
              <h3 className="text-3xl md:text-4xl font-bold mb-4 leading-tight">
                ¿Listo para tener unas redes<br className="hidden md:block" /> que dan gusto ver?
              </h3>
              <p className="text-lg text-muted-foreground mb-8 max-w-xl mx-auto">
                Elige tu plan y preparamos tu primer mes de contenido en los próximos 14 días.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
                <Link to="/#pricing">
                  <Button size="lg" className="gap-2 text-base px-8 shadow-glow">
                    Contratar ahora
                    <ArrowRight size={16} />
                  </Button>
                </Link>
                <Link to="/#contact">
                  <Button size="lg" variant="outline" className="gap-2 text-base px-8">
                    Agendar reunión
                  </Button>
                </Link>
              </div>
              <div className="flex flex-wrap justify-center gap-4 text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><CheckCircle2 size={12} className="text-primary" /> Sin permanencia</span>
                <span className="flex items-center gap-1"><CheckCircle2 size={12} className="text-primary" /> 8 piezas/mes</span>
                <span className="flex items-center gap-1"><CheckCircle2 size={12} className="text-primary" /> Listo en 14 días</span>
              </div>
            </CardContent>
          </Card>
        </AnimateIn>
      </section>
    </div>
  );
};

export default SocialDemo;
